"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAppBridgeHost = createAppBridgeHost;
const protocol_1 = require("./protocol");
function createAppBridgeHost(options) {
    const now = options.now ?? (() => new Date());
    const requestId = options.requestId ?? (() => globalThis.crypto.randomUUID());
    let established = false;
    const reply = (target, request, type, payload) => {
        const message = protocol_1.appBridgeHostMessageSchema.parse({
            protocolVersion: protocol_1.APP_BRIDGE_PROTOCOL_VERSION,
            requestId: request.requestId,
            installationId: request.installationId,
            timestamp: now().toISOString(),
            type,
            payload,
        });
        target.postMessage(message, options.appOrigin);
    };
    return {
        async handleMessage(event) {
            if (event.origin !== options.appOrigin ||
                event.source !== options.expectedAppWindow) {
                return false;
            }
            const replyTarget = messageTarget(event.source);
            if (!replyTarget)
                return false;
            const identity = protocol_1.appBridgeMessageIdentitySchema.safeParse(event.data);
            if (identity.success &&
                identity.data.protocolVersion !== protocol_1.APP_BRIDGE_PROTOCOL_VERSION) {
                reply(replyTarget, identity.data, "CREWFSM_ERROR", {
                    code: "UNSUPPORTED_PROTOCOL",
                    message: `App Bridge protocol ${identity.data.protocolVersion} is not supported.`,
                });
                return true;
            }
            const parsed = protocol_1.appBridgeClientMessageSchema.safeParse(event.data);
            if (!parsed.success)
                return false;
            const message = parsed.data;
            if (message.installationId !== options.installationId) {
                reply(replyTarget, message, "CREWFSM_ERROR", {
                    code: "INSTALLATION_MISMATCH",
                    message: "The bridge installation does not match this host.",
                });
                return true;
            }
            try {
                if (message.type === "CREWFSM_APP_READY") {
                    const session = await options.createSession();
                    established = true;
                    reply(replyTarget, message, "CREWFSM_CONTEXT", {
                        context: session.context,
                        sessionToken: session.sessionToken,
                        expiresAt: session.expiresAt.toISOString(),
                    });
                    return true;
                }
                if (!established) {
                    reply(replyTarget, message, "CREWFSM_ERROR", {
                        code: "SESSION_REQUIRED",
                        message: "Complete the App Bridge READY handshake first.",
                    });
                    return true;
                }
                if (message.type === "CREWFSM_REFRESH_CONTEXT") {
                    const session = await options.createSession();
                    reply(replyTarget, message, "CREWFSM_CONTEXT", {
                        context: session.context,
                        sessionToken: session.sessionToken,
                        expiresAt: session.expiresAt.toISOString(),
                    });
                    return true;
                }
                await dispatchCommand(message, options.handlers ?? {});
                reply(replyTarget, message, "CREWFSM_ACK", {
                    acknowledgedType: message.type,
                });
                return true;
            }
            catch (error) {
                reply(replyTarget, message, "CREWFSM_ERROR", {
                    code: "COMMAND_FAILED",
                    message: bridgeFailureMessage(error),
                });
                return true;
            }
        },
        synchronizeRoute(currentRoute) {
            if (!established)
                return false;
            const message = protocol_1.appBridgeHostMessageSchema.safeParse({
                protocolVersion: protocol_1.APP_BRIDGE_PROTOCOL_VERSION,
                requestId: requestId(),
                installationId: options.installationId,
                timestamp: now().toISOString(),
                type: "CREWFSM_ROUTE_CHANGED",
                payload: { currentRoute },
            });
            if (!message.success)
                return false;
            options.appWindow.postMessage(message.data, options.appOrigin);
            return true;
        },
    };
}
function messageTarget(value) {
    if (typeof value !== "object" ||
        value === null ||
        !("postMessage" in value) ||
        typeof value.postMessage !== "function") {
        return null;
    }
    return value;
}
function bridgeFailureMessage(error) {
    const fallback = "App Bridge command failed.";
    if (!(error instanceof Error))
        return fallback;
    const message = error.message.trim();
    if (!message)
        return fallback;
    return message.length <= 240 ? message : `${message.slice(0, 237)}...`;
}
async function dispatchCommand(message, handlers) {
    switch (message.type) {
        case "CREWFSM_NAVIGATE":
            await handlers.navigate?.(message.payload.path);
            return;
        case "CREWFSM_OPEN_RESOURCE":
            await handlers.openResource?.(message.payload.resourceType, message.payload.resourceId);
            return;
        case "CREWFSM_SET_TITLE":
            await handlers.setTitle?.(message.payload.title);
            return;
        case "CREWFSM_SET_LOADING":
            await handlers.setLoading?.(message.payload.loading);
            return;
        case "CREWFSM_SHOW_TOAST":
            await handlers.showToast?.(message.payload.message, message.payload.tone);
            return;
        case "CREWFSM_OPEN_MODAL":
            await handlers.openModal?.(message.payload.modalId);
            return;
        case "CREWFSM_CLOSE_MODAL":
            await handlers.closeModal?.(message.payload.modalId);
            return;
        case "CREWFSM_REQUEST_RESIZE":
            await handlers.requestResize?.(message.payload.height);
            return;
    }
}
//# sourceMappingURL=host.js.map